import * as XLSX from "xlsx";

/**
 * Export a BOQ (with lines + rate analysis map) to an .xlsx file, matching the
 * Riverwood workbook structure: Budget BOQ sheet + RA working sheet.
 *
 * @param {object} args
 * @param {object} args.project     { name, location, ... }
 * @param {object} args.boq         { version, status, lines: [...] }
 * @param {object} args.raMap       { [iow_code]: { entries, ra_rate } }
 */
export function exportBOQToXlsx({ project, boq, raMap }) {
  const wb = XLSX.utils.book_new();
  const projectName = project?.name || "Project";
  const version = boq?.version || 0;
  const status = boq?.status || "Draft";

  // --- Sheet 1: Budget BOQ ---
  const rows = [];
  // Title rows
  rows.push([`${projectName}  ·  Budget BOQ  ·  v${version} (${status})`]);
  rows.push([`Generated ${new Date().toLocaleString("en-IN")}`]);
  rows.push([]);
  // Main header (row index 3) + sub-header (row index 4) — Riverwood-style
  const H = [
    "SL.NO", "IOW Code", "DESCRIPTION OF ITEMS", "UNIT",
    "Budget", "", "",       // 4,5,6 → Qty, Rate, Amount
    "GFC Qty",              // 7
    "Actual (JCR)", "", "", // 8,9,10 → Qty, Rate, Amount
    "Balance", "", "",      // 11,12,13 → Qty, Rate, Amount
    "CTC", "", "",          // 14,15,16 → Qty, Rate, Amount
    "RA",                   // 17
  ];
  const SUB = [
    "", "", "", "",
    "Qty", "Rate", "Amount",
    "",
    "Qty", "Rate", "Amount",
    "Qty", "Rate", "Amount",
    "Qty", "Rate", "Amount",
    "",
  ];
  rows.push(H);
  rows.push(SUB);

  // Group lines by category + sub-category preserving original order
  const grouped = {};
  const catOrder = [];
  const subOrder = {};
  for (const l of boq?.lines || []) {
    const cat = l.category || "General";
    const sub = l.sub_category || "";
    if (!(cat in grouped)) { grouped[cat] = {}; catOrder.push(cat); subOrder[cat] = []; }
    if (!(sub in grouped[cat])) { grouped[cat][sub] = []; subOrder[cat].push(sub); }
    grouped[cat][sub].push(l);
  }

  let catNum = 0;
  let totals = { budget: 0, jcr: 0, balance: 0, ctc: 0 };
  for (const cat of catOrder) {
    catNum += 1;
    rows.push([catNum, "", cat, "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]);
    for (const sub of subOrder[cat]) {
      if (sub) {
        rows.push(["", "", sub, "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]);
      }
      for (const l of grouped[cat][sub]) {
        const bQty = Number(l.qty || 0);
        const bRate = Number(l.rate || 0);
        const bAmt = bQty * bRate;
        const jQty = Number(l.jcr_qty || 0);
        const jRate = Number(l.jcr_rate || 0);
        const jAmt = jQty * jRate;
        const gfc = Number(l.gfc_qty || 0);
        const balQty = Math.max(0, bQty - jQty);
        const balAmt = balQty * bRate;
        const ctcQty = gfc > 0 ? Math.max(0, gfc - jQty) : balQty;
        const ctcAmt = ctcQty * bRate;
        totals.budget += bAmt; totals.jcr += jAmt; totals.balance += balAmt; totals.ctc += ctcAmt;
        rows.push([
          "", l.iow_code, l.description || "", l.uom || "",
          bQty, bRate, bAmt,
          gfc,
          jQty, jRate, jAmt,
          balQty, bRate, balAmt,
          ctcQty, bRate, ctcAmt,
          l.has_ra ? "Y" : "N",
        ]);
      }
    }
  }
  // Grand totals row
  rows.push([]);
  rows.push(["", "", "GRAND TOTAL", "",
    "", "", totals.budget,
    "",
    "", "", totals.jcr,
    "", "", totals.balance,
    "", "", totals.ctc,
    "",
  ]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  // Column widths for readability
  ws["!cols"] = [
    { wch: 6 }, { wch: 10 }, { wch: 46 }, { wch: 8 },
    { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 10 },
    { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 6 },
  ];
  // Merge top-header group cells (Budget / Actual JCR / Balance / CTC)
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 17 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 17 } },
    { s: { r: 3, c: 4 }, e: { r: 3, c: 6 } },
    { s: { r: 3, c: 8 }, e: { r: 3, c: 10 } },
    { s: { r: 3, c: 11 }, e: { r: 3, c: 13 } },
    { s: { r: 3, c: 14 }, e: { r: 3, c: 16 } },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Budget BOQ");

  // --- Sheet 2: RA working (Rate Analysis) ---
  const raRows = [];
  raRows.push([`${projectName}  ·  Rate Analysis`]);
  raRows.push([]);
  raRows.push([
    "IOW Code", "Type", "IOW UOM", "IOW description",
    "Co-Eff", "Resource", "Resource UOM", "Resource Rate", "Cost / Unit",
    "IOW Qty", "Total Resource", "Resource Value",
  ]);
  // Iterate BOQ lines to keep the order consistent with the BOQ sheet
  for (const l of boq?.lines || []) {
    const ra = raMap?.[l.iow_code];
    if (!ra || !ra.entries || ra.entries.length === 0) continue;
    let first = true;
    for (const e of ra.entries) {
      const coeff = Number(e.co_eff || 0);
      const resRate = Number(e.resource_rate || 0);
      const iowQty = Number(l.qty || 0);
      const costPerUnit = coeff * resRate;
      const totalRes = coeff * iowQty;
      const resValue = totalRes * resRate;
      raRows.push([
        first ? l.iow_code : "",
        e.type || "",
        first ? (l.uom || "") : "",
        first ? (l.description || "") : "",
        coeff,
        e.resource || "",
        e.resource_uom || "",
        resRate,
        costPerUnit,
        first ? iowQty : "",
        totalRes,
        resValue,
      ]);
      first = false;
    }
    // Subtotal row per IOW (RA Rate)
    raRows.push([
      "", "", "", "  → RA Rate for " + l.iow_code,
      "", "", "", "", Number(ra.ra_rate || 0),
      "", "", "",
    ]);
    raRows.push([]);
  }
  const raWs = XLSX.utils.aoa_to_sheet(raRows);
  raWs["!cols"] = [
    { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 42 },
    { wch: 10 }, { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    { wch: 10 }, { wch: 12 }, { wch: 14 },
  ];
  raWs["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 11 } }];
  XLSX.utils.book_append_sheet(wb, raWs, "RA working");

  // Download
  const safe = projectName.replace(/[^A-Za-z0-9_-]+/g, "_");
  const fileName = `${safe}_BOQ_v${version}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, fileName);
}
